import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { automations as api, type Automation, type FlowStep } from "@/lib/api";
import StatusBadge from "@/components/StatusBadge";
import Spinner from "@/components/Spinner";
import {
  Plus, Zap, Play, Pause, Trash2, X, Clock, GitBranch, Mail,
  ArrowDown, Linkedin, MessageSquare, ChevronRight, Settings2, Download,
} from "lucide-react";
import clsx from "clsx";

// ── Helpers ───────────────────────────────────────────────────────────────────
function createStep(type: string): FlowStep {
  switch (type) {
    case "trigger":          return { type: "trigger", triggerType: "subscriber_added" };
    case "delay":            return { type: "delay", delayMinutes: 60 };
    case "condition":        return { type: "condition", conditionType: "has_tag", config: { value: "" }, yesBranch: 0, noBranch: 0 };
    case "send":             return { type: "send", subject: "Your subject", htmlContent: "<h1>Hello!</h1><p>Your content here.</p>" };
    case "linkedin_message": return { type: "linkedin_message", messageTemplate: "Hi {{first_name}}, just following up on my email!" };
    case "sms_message":      return { type: "sms_message", messageTemplate: "Hi {{first_name}}, did you see my email?", phoneField: "phone" };
    default:                 return { type: "delay", delayMinutes: 60 };
  }
}

function formatDelay(m: number) {
  if (m < 60)   return `${m}m`;
  if (m < 1440) return `${Math.round(m / 60)}h`;
  return `${Math.round(m / 1440)}d`;
}

const STEP_META: Record<string, { label: string; color: string; icon: React.ElementType }> = {
  trigger:          { label: "Trigger",   color: "bg-amber-50 text-amber-600 border border-amber-200",    icon: Zap           },
  delay:            { label: "Wait",      color: "bg-blue-50 text-blue-600 border border-blue-200",        icon: Clock         },
  condition:        { label: "Condition", color: "bg-purple-50 text-purple-600 border border-purple-200",  icon: GitBranch     },
  send:             { label: "Email",     color: "bg-coral-light text-coral border border-coral/20",       icon: Mail          },
  linkedin_message: { label: "LinkedIn",  color: "bg-[#EEF4FF] text-[#0A66C2] border border-[#0A66C2]/20",icon: Linkedin      },
  sms_message:      { label: "SMS",       color: "bg-green-50 text-green-600 border border-green-200",     icon: MessageSquare },
};

const ADD_STEP_TYPES = [
  { type: "delay",            label: "Wait",       icon: Clock,          desc: "Pause for a set time" },
  { type: "condition",        label: "Condition",  icon: GitBranch,      desc: "Branch based on behaviour" },
  { type: "send",             label: "Email",      icon: Mail,           desc: "Send an email" },
  { type: "linkedin_message", label: "LinkedIn",   icon: Linkedin,       desc: "Send a LinkedIn message" },
  { type: "sms_message",      label: "SMS",        icon: MessageSquare,  desc: "Send an SMS" },
];

// ── Step node in flow ─────────────────────────────────────────────────────────
function StepNode({ step, index, total, onRemove, onEdit }: {
  step: FlowStep; index: number; total: number;
  onRemove: () => void; onEdit: () => void;
}) {
  const meta = STEP_META[step.type] || STEP_META.delay;
  const Icon = meta.icon;

  const summary = step.type === "trigger"          ? (() => {
      const t = (step.triggerType || "trigger").replace(/_/g, " ");
      const cfg = step.config || {};
      if (step.triggerType === "inactivity" && cfg.inactivityDays) return `inactive ${cfg.inactivityDays}d`;
      if (step.triggerType === "webhook_received" && cfg.webhookEvent) return `webhook: ${cfg.webhookEvent}`;
      if (step.triggerType === "link_clicked" && cfg.url) return `clicked ${cfg.url}`;
      if (cfg.campaignId) return `${t} (filtered)`;
      if (cfg.tag) return `tag: ${cfg.tag}`;
      return t;
    })()
    : step.type === "delay"                        ? `Wait ${formatDelay(step.delayMinutes || 60)}`
    : step.type === "send"                         ? (step.subject || "Email step")
    : step.type === "condition"                    ? `If ${(step.conditionType || "condition").replace(/_/g, " ")}`
    : step.type === "linkedin_message"             ? "Send LinkedIn message"
    : step.type === "sms_message"                  ? "Send SMS"
    : "Step";

  return (
    <div className="flex flex-col items-center">
      <div className={clsx(
        "group relative w-full max-w-xs rounded-2xl p-4 flex items-start gap-3 transition-all hover:shadow-md cursor-pointer",
        meta.color
      )} onClick={onEdit}>
        <div className="w-8 h-8 rounded-xl bg-white/60 flex items-center justify-center flex-shrink-0">
          <Icon size={16} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[11px] font-bold uppercase tracking-wider opacity-60">{meta.label}</p>
          <p className="text-sm font-semibold mt-0.5 truncate">{summary}</p>
        </div>
        {index > 0 && (
          <button onClick={(e) => { e.stopPropagation(); onRemove(); }}
            className="opacity-0 group-hover:opacity-100 p-1 rounded-lg hover:bg-black/[0.08] transition-all">
            <X size={13} />
          </button>
        )}
      </div>
      {index < total - 1 && (
        <div className="flex flex-col items-center py-1 text-slate/30">
          <div className="w-px h-4 bg-black/[0.08]" />
          <ArrowDown size={14} />
          <div className="w-px h-4 bg-black/[0.08]" />
        </div>
      )}
    </div>
  );
}

// ── Step editor drawer ────────────────────────────────────────────────────────
function StepEditorDrawer({ step, onSave, onClose }: {
  step: FlowStep; onSave: (s: FlowStep) => void; onClose: () => void;
}) {
  const [s, setS] = useState<FlowStep>({ ...step });
  const inp = "w-full px-4 py-2.5 rounded-xl border border-black/[0.08] bg-paper text-sm focus:outline-none focus:ring-2 focus:ring-coral/30";
  const sel = `${inp} appearance-none`;
  const meta = STEP_META[s.type] || STEP_META.delay;

  return (
    <div className="fixed inset-0 z-50 flex justify-end" onClick={onClose}>
      <div className="absolute inset-0 bg-black/30 backdrop-blur-sm" />
      <div className="relative w-full max-w-sm bg-white shadow-2xl flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-black/[0.06]">
          <div className="flex items-center gap-2">
            <meta.icon size={16} />
            <h3 className="font-bold text-ink">{meta.label} settings</h3>
          </div>
          <button onClick={onClose} className="p-2 rounded-xl text-slate hover:text-ink hover:bg-black/[0.04]"><X size={16} /></button>
        </div>
        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {s.type === "trigger" && (
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-slate mb-1.5">Trigger type</label>
                <select value={s.triggerType || ""} onChange={(e) => setS({ ...s, triggerType: e.target.value as any, config: {} })} className={sel}>
                  <optgroup label="Subscriber Events">
                    <option value="subscriber_added">subscriber added</option>
                    <option value="tag_added">tag added</option>
                  </optgroup>
                  <optgroup label="Email Behavior">
                    <option value="email_opened">email opened</option>
                    <option value="link_clicked">link clicked</option>
                    <option value="campaign_sent">campaign sent</option>
                    <option value="reply_received">reply received</option>
                  </optgroup>
                  <optgroup label="Engagement">
                    <option value="inactivity">inactivity (no opens)</option>
                  </optgroup>
                  <optgroup label="External">
                    <option value="webhook_received">webhook received</option>
                    <option value="manual">manual trigger</option>
                  </optgroup>
                </select>
              </div>
              {s.triggerType === "tag_added" && (
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-slate mb-1.5">Tag name</label>
                  <input className={inp} placeholder="e.g. vip, purchased" value={s.config?.tag || ""}
                    onChange={(e) => setS({ ...s, config: { ...s.config, tag: e.target.value } })} />
                </div>
              )}
              {(s.triggerType === "email_opened" || s.triggerType === "link_clicked" || s.triggerType === "campaign_sent" || s.triggerType === "reply_received") && (
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-slate mb-1.5">Campaign ID (optional)</label>
                  <input className={inp} placeholder="Leave blank for any campaign" value={s.config?.campaignId || ""}
                    onChange={(e) => setS({ ...s, config: { ...s.config, campaignId: e.target.value } })} />
                  <p className="text-[10px] text-slate mt-1">Filter to a specific campaign, or leave blank to trigger on any</p>
                </div>
              )}
              {s.triggerType === "link_clicked" && (
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-slate mb-1.5">URL contains (optional)</label>
                  <input className={inp} placeholder="e.g. /pricing or /signup" value={s.config?.url || ""}
                    onChange={(e) => setS({ ...s, config: { ...s.config, url: e.target.value } })} />
                  <p className="text-[10px] text-slate mt-1">Trigger only when the clicked URL contains this text</p>
                </div>
              )}
              {s.triggerType === "inactivity" && (
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-slate mb-1.5">Days of inactivity</label>
                  <input type="number" min={1} max={365} className={inp} placeholder="30" value={s.config?.inactivityDays || 30}
                    onChange={(e) => setS({ ...s, config: { ...s.config, inactivityDays: parseInt(e.target.value) || 30 } })} />
                  <p className="text-[10px] text-slate mt-1">Trigger when subscriber hasn't opened any email for this many days</p>
                </div>
              )}
              {s.triggerType === "webhook_received" && (
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-slate mb-1.5">Event name</label>
                  <input className={inp} placeholder="e.g. purchase, payment_completed" value={s.config?.webhookEvent || ""}
                    onChange={(e) => setS({ ...s, config: { ...s.config, webhookEvent: e.target.value } })} />
                  <p className="text-[10px] text-slate mt-1">
                    Fire via API: POST /api/automations/trigger/webhook with {`{event: "${s.config?.webhookEvent || "purchase"}", email: "..."}`}
                  </p>
                </div>
              )}
            </div>
          )}
          {s.type === "delay" && (
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-slate mb-1.5">Delay duration</label>
              <div className="flex gap-2">
                <input type="number" min={1} value={s.delayMinutes || 60}
                  onChange={(e) => setS({ ...s, delayMinutes: parseInt(e.target.value) || 60 })}
                  className={`${inp} flex-1`} />
                <select defaultValue="minutes" className={`${sel} w-32`}
                  onChange={(e) => {
                    const v = e.target.value;
                    const cur = s.delayMinutes || 60;
                    setS({ ...s, delayMinutes: v === "hours" ? cur * 60 : v === "days" ? cur * 1440 : cur });
                  }}>
                  <option value="minutes">Minutes</option>
                  <option value="hours">Hours</option>
                  <option value="days">Days</option>
                </select>
              </div>
            </div>
          )}
          {s.type === "condition" && (
            <>
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-slate mb-1.5">Condition</label>
                <select value={s.conditionType || ""} onChange={(e) => setS({ ...s, conditionType: e.target.value as any })} className={sel}>
                  <option value="has_tag">has tag</option>
                  <option value="opened_email">opened email</option>
                  <option value="clicked_email">clicked email</option>
                  <option value="has_replied">has replied</option>
                  <option value="status_is">status is</option>
                  <option value="opened_previous">opened previous (legacy)</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-slate mb-1.5">
                  {s.conditionType === "has_tag" ? "Tag name" :
                   s.conditionType === "status_is" ? "Status value" :
                   "Campaign ID (optional)"}
                </label>
                <input value={s.config?.value || ""} onChange={(e) => setS({ ...s, config: { ...s.config, value: e.target.value } })}
                  placeholder={
                    s.conditionType === "has_tag" ? "e.g. vip" :
                    s.conditionType === "status_is" ? "e.g. active" :
                    "Leave blank for any"
                  }
                  className={inp} />
              </div>
            </>
          )}
          {s.type === "send" && (
            <>
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-slate mb-1.5">Subject line</label>
                <input value={s.subject || ""} onChange={(e) => setS({ ...s, subject: e.target.value })} className={inp} placeholder="Your subject…" />
              </div>
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-slate mb-1.5">HTML content</label>
                <textarea rows={6} value={s.htmlContent || ""} onChange={(e) => setS({ ...s, htmlContent: e.target.value })}
                  className={`${inp} resize-none font-mono text-xs`} placeholder="<h1>Hello {{first_name}}</h1>" />
              </div>
            </>
          )}
          {s.type === "linkedin_message" && (
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-slate mb-1.5">Message template</label>
              <textarea rows={4} value={s.messageTemplate || ""} onChange={(e) => setS({ ...s, messageTemplate: e.target.value })}
                className={`${inp} resize-none`} placeholder="Hi {{first_name}}, …" />
            </div>
          )}
          {s.type === "sms_message" && (
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-slate mb-1.5">Message template</label>
              <textarea rows={4} value={s.messageTemplate || ""} onChange={(e) => setS({ ...s, messageTemplate: e.target.value })}
                className={`${inp} resize-none`} placeholder="Hi {{first_name}}, …" />
            </div>
          )}
        </div>
        <div className="px-5 py-4 border-t border-black/[0.06]">
          <button onClick={() => onSave(s)}
            className="w-full py-2.5 rounded-full bg-coral text-white text-sm font-semibold hover:bg-coral-dark transition-colors shadow-md shadow-coral/20">
            Save step
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Automation flow builder ───────────────────────────────────────────────────
function FlowBuilder({ automation, onSave, onClose }: {
  automation: Automation | null; onSave: () => void; onClose: () => void;
}) {
  const [name, setName]     = useState(automation?.name || "");
  const [steps, setSteps]   = useState<FlowStep[]>(
    automation?.flow_data?.steps || [createStep("trigger")]
  );
  const [saving, setSaving] = useState(false);
  const [addingAfter, setAddingAfter] = useState<number | null>(null);
  const [editingIdx, setEditingIdx]   = useState<number | null>(null);

  const addStep = (type: string, after: number) => {
    const s = [...steps];
    s.splice(after + 1, 0, createStep(type));
    setSteps(s);
    setAddingAfter(null);
  };

  const removeStep = (idx: number) => setSteps(steps.filter((_, i) => i !== idx));

  const updateStep = (idx: number, s: FlowStep) => {
    const ns = [...steps];
    ns[idx] = s;
    setSteps(ns);
    setEditingIdx(null);
  };

  const save = async () => {
    if (!name.trim()) return;
    setSaving(true);
    try {
      if (automation) {
        await api.update(automation.id, { name, flow_data: { steps } });
      } else {
        await api.create({ name, trigger_type: "subscriber_added", flow_data: { steps } });
      }
      onSave();
    } catch (e: any) { alert(e.message || "Save failed"); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-stretch sm:items-center justify-center p-0 sm:p-4">
      <div className="w-full sm:max-w-2xl bg-white sm:rounded-2xl shadow-2xl flex flex-col max-h-screen sm:max-h-[90vh]">
        {/* Header */}
        <div className="flex items-center justify-between px-5 sm:px-6 py-4 border-b border-black/[0.06]">
          <div className="flex items-center gap-3 flex-1 min-w-0 mr-3">
            <div className="p-2 rounded-xl bg-amber-50">
              <Zap size={16} className="text-amber-600" />
            </div>
            <input value={name} onChange={(e) => setName(e.target.value)}
              placeholder="Automation name…"
              className="font-bold text-ink bg-transparent border-none outline-none flex-1 min-w-0 text-base" />
          </div>
          <button onClick={onClose} className="p-2 rounded-xl text-slate hover:text-ink hover:bg-black/[0.04]">
            <X size={18} />
          </button>
        </div>

        {/* Flow canvas */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="flex flex-col items-center gap-0 max-w-xs mx-auto">
            {steps.map((step, i) => (
              <div key={i} className="w-full">
                <StepNode step={step} index={i} total={steps.length}
                  onRemove={() => removeStep(i)}
                  onEdit={() => setEditingIdx(i)} />
                {/* Add step button */}
                <div className="flex flex-col items-center py-1">
                  <div className="w-px h-3 bg-black/[0.08]" />
                  <button onClick={() => setAddingAfter(addingAfter === i ? null : i)}
                    className="w-7 h-7 rounded-full border-2 border-dashed border-black/[0.15] flex items-center justify-center text-slate hover:border-coral hover:text-coral transition-colors">
                    <Plus size={13} />
                  </button>
                  {addingAfter === i && (
                    <div className="mt-3 w-full max-w-xs bg-white border border-black/[0.08] rounded-2xl shadow-xl overflow-hidden">
                      {ADD_STEP_TYPES.map(({ type, label, icon: Icon, desc }) => (
                        <button key={type} onClick={() => addStep(type, i)}
                          className="flex items-center gap-3 w-full px-4 py-3 text-left hover:bg-black/[0.02] transition-colors border-b border-black/[0.04] last:border-0">
                          <div className={clsx("p-1.5 rounded-lg", STEP_META[type]?.color)}>
                            <Icon size={14} />
                          </div>
                          <div>
                            <p className="text-sm font-semibold text-ink">{label}</p>
                            <p className="text-xs text-slate">{desc}</p>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                  {addingAfter === i && <div className="w-px h-3 bg-black/[0.08]" />}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="px-5 sm:px-6 py-4 border-t border-black/[0.06] flex gap-3">
          <button onClick={onClose}
            className="flex-1 py-2.5 rounded-full border border-black/[0.08] text-sm font-semibold hover:border-black/[0.14]">
            Cancel
          </button>
          <button onClick={save} disabled={saving || !name.trim()}
            className="flex-1 py-2.5 rounded-full bg-coral text-white text-sm font-semibold hover:bg-coral-dark disabled:opacity-60 shadow-md shadow-coral/20">
            {saving ? "Saving…" : automation ? "Save changes" : "Create automation"}
          </button>
        </div>
      </div>

      {/* Step editor drawer */}
      {editingIdx !== null && (
        <StepEditorDrawer
          step={steps[editingIdx]}
          onSave={(s) => updateStep(editingIdx, s)}
          onClose={() => setEditingIdx(null)}
        />
      )}
    </div>
  );
}

// ── Automation list card ──────────────────────────────────────────────────────
function AutomationCard({ automation, onEdit, onToggle, onDelete, toggling, deleting }: {
  automation: Automation; onEdit: () => void; onToggle: () => void; onDelete: () => void;
  toggling: boolean; deleting: boolean;
}) {
  const isActive  = automation.status === "active";
  const steps     = automation.flow_data?.steps ?? [];
  const stepCount = steps.length;

  return (
    <div className="bg-white rounded-2xl border border-black/[0.06] p-5 hover:shadow-md hover:shadow-black/[0.04] transition-shadow">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <div className={clsx("p-2.5 rounded-xl flex-shrink-0", isActive ? "bg-amber-50" : "bg-black/[0.04]")}>
            <Zap size={16} className={isActive ? "text-amber-600" : "text-slate"} />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-bold text-ink truncate">{automation.name}</p>
            <p className="text-xs text-slate mt-0.5">
              {stepCount} step{stepCount !== 1 ? "s" : ""}
              {automation.run_count ? ` · ${automation.run_count} runs` : ""}
            </p>
          </div>
        </div>
        <StatusBadge status={automation.status} />
      </div>

      {/* Step preview */}
      {steps.length > 0 && (
        <div className="flex items-center gap-1.5 mb-4 overflow-x-auto pb-1">
          {steps.slice(0, 5).map((s, i) => {
            const meta = STEP_META[s.type] || STEP_META.delay;
            const Icon = meta.icon;
            return (
              <div key={i} className="flex items-center gap-1.5 flex-shrink-0">
                <div className={clsx("p-1.5 rounded-lg", meta.color)}>
                  <Icon size={12} />
                </div>
                {i < Math.min(steps.length - 1, 4) && (
                  <ChevronRight size={12} className="text-slate/30 flex-shrink-0" />
                )}
              </div>
            );
          })}
          {steps.length > 5 && (
            <span className="text-xs text-slate flex-shrink-0">+{steps.length - 5} more</span>
          )}
        </div>
      )}

      <div className="flex items-center gap-2">
        <button onClick={onEdit}
          className="flex-1 flex items-center justify-center gap-2 py-2 rounded-xl border border-black/[0.08] text-xs font-semibold text-slate hover:text-ink hover:border-black/[0.14] transition-colors">
          <Settings2 size={13} /> Edit
        </button>
        <button onClick={onToggle} disabled={toggling}
          className={clsx("flex-1 flex items-center justify-center gap-2 py-2 rounded-xl text-xs font-semibold transition-colors disabled:opacity-50",
            isActive
              ? "bg-amber-50 text-amber-700 hover:bg-amber-100"
              : "bg-teal/10 text-teal hover:bg-teal/15")}>
          {isActive ? <><Pause size={12} /> Pause</> : <><Play size={12} /> Activate</>}
        </button>
        <button onClick={onDelete} disabled={deleting}
          className="p-2 rounded-xl text-red-400 hover:bg-red-50 transition-colors disabled:opacity-50">
          <Trash2 size={14} />
        </button>
      </div>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────
export default function AutomationsPage() {
  const [items, setItems]       = useState<Automation[]>([]);
  const [loading, setLoading]   = useState(true);
  const [builder, setBuilder]   = useState<Automation | null | "new">(null);
  const [toggling, setToggling] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await api.list(); setItems(r.automations); }
    catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleToggle = async (a: Automation) => {
    setToggling(a.id);
    try {
      const newStatus = a.status === "active" ? "paused" : "active";
      await api.update(a.id, { status: newStatus });
      setItems((prev) => prev.map((x) => x.id === a.id ? { ...x, status: newStatus } : x));
    } catch (e: any) { alert(e.message || "Failed"); }
    finally { setToggling(null); }
  };

  const handleDelete = async (id: string) => {
    if (!confirm("Delete this automation?")) return;
    setDeleting(id);
    try { await api.delete(id); setItems((p) => p.filter((x) => x.id !== id)); }
    catch (e: any) { alert(e.message || "Delete failed"); }
    finally { setDeleting(null); }
  };

  return (
    <div>
      <div className="sticky top-0 z-10 bg-paper/90 backdrop-blur-md border-b border-black/[0.06] px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
        <div>
          <h1 className="text-lg sm:text-xl font-bold text-ink">Automations</h1>
          <p className="text-xs sm:text-sm text-slate mt-0.5">{items.length} flow{items.length !== 1 ? "s" : ""}</p>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/automations/prebuilt"
            className="hidden sm:inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-teal-light border border-teal/20 text-sm font-semibold text-teal hover:bg-teal/10 hover:border-teal/40 transition-colors">
            <Download size={14} /> Templates
          </Link>
          <button onClick={() => setBuilder("new")}
            className="inline-flex items-center gap-2 px-4 sm:px-5 py-2.5 rounded-full bg-coral text-white text-sm font-semibold hover:bg-coral-dark transition-colors shadow-md shadow-coral/25">
            <Plus size={15} />
            <span className="hidden sm:inline">New Automation</span>
            <span className="sm:hidden">New</span>
          </button>
        </div>
      </div>

      <div className="p-4 sm:p-6 lg:p-8 max-w-5xl mx-auto">
        {loading ? <Spinner className="py-20" /> : items.length === 0 ? (
          <div className="bg-white rounded-2xl border border-black/[0.06] py-16 sm:py-20 text-center">
            <div className="w-14 h-14 rounded-2xl bg-amber-50 flex items-center justify-center mx-auto mb-4">
              <Zap size={24} className="text-amber-600" />
            </div>
            <p className="font-bold text-ink mb-1">No automations yet</p>
            <p className="text-sm text-slate mb-6 max-w-xs mx-auto">
              Automations send emails, LinkedIn messages, and SMS based on subscriber behaviour.
            </p>
            <button onClick={() => setBuilder("new")}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-coral text-white text-sm font-semibold hover:bg-coral-dark shadow-md shadow-coral/20">
              <Plus size={15} /> Create first automation
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {items.map((a) => (
              <AutomationCard key={a.id} automation={a}
                onEdit={() => setBuilder(a)}
                onToggle={() => handleToggle(a)}
                onDelete={() => handleDelete(a.id)}
                toggling={toggling === a.id}
                deleting={deleting === a.id}
              />
            ))}
          </div>
        )}
      </div>

      {/* Flow builder modal */}
      {builder !== null && (
        <FlowBuilder
          automation={builder === "new" ? null : builder}
          onSave={() => { setBuilder(null); load(); }}
          onClose={() => setBuilder(null)}
        />
      )}
    </div>
  );
}
